import React from 'react';
import { useTheme } from '@emotion/react';
import { Box, Chip, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import { Add, Check, Close, DeleteOutlineRounded, DragIndicator, InfoOutlined } from '@mui/icons-material';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import CustomInputField from 'ui-components/CustomInputField';
import { planVersionInfo } from 'utils/index';
import { Secondary } from 'ui-components/Buttons';
import CellValuePopover from './CellValuePopover';
import PlanLabel from './PlanLabel';

// Ids are stored with the values and used as react keys, so one is slugged from
// its label when the row is created and then left alone however the label changes.
const slugify = (text, taken) => {
    const base = String(text || 'row')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 48) || 'row';

    let candidate = base;
    let suffix = 2;
    while (taken.includes(candidate)) candidate = `${base}_${suffix++}`;
    return candidate;
};

const ValueCell = ({ value, onChange, label }) => {
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = React.useState(null);
    const isText = typeof value === 'string';

    return (
        <>
            <Box
                onClick={(event) => setAnchorEl(event.currentTarget)}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setAnchorEl(event.currentTarget);
                    }
                }}
                tabIndex={0}
                role="button"
                aria-label={label}
                sx={{
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    borderRadius: '6px',
                    '&:hover': { backgroundColor: theme.palette.customColors.indigo50 },
                    '&:focus-visible': { outline: `2px solid ${theme.palette.customColors.indigo700}`, outlineOffset: '-2px' },
                }}
            >
                {isText ? (
                    <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: theme.palette.customColors.g300, textAlign: 'center' }}>
                        {value || '—'}
                    </Typography>
                ) : value === true ? (
                    <Check fontSize="small" sx={{ color: theme.palette.customColors.b300 }} />
                ) : (
                    <Close fontSize="small" sx={{ color: theme.palette.customColors.g75 }} />
                )}
            </Box>

            <CellValuePopover
                anchorEl={anchorEl}
                value={value}
                onChange={onChange}
                onClose={() => setAnchorEl(null)}
            />
        </>
    );
};

/**
 * Edits the "Compare All Features" table as the table it becomes: plans across
 * the top, features down the side. Values are stored per plan, so adding or
 * removing a column never shifts another column's values.
 */
/** "Plus V1" where a plan has been superseded, "Starter" where it has not. */
const columnLabel = (plans, planKey) => {
    const title = plans?.[planKey]?.title || planKey;
    const { version, showVersion } = planVersionInfo({ plans }, planKey);
    return showVersion ? `${title} V${version}` : title;
};

const ComparisonEditor = ({ comparison, plans, onChange, problemsFor }) => {
    const theme = useTheme();
    const safeComparison = comparison || { columns: [], categories: [] };
    const safeCategories = Array.isArray(safeComparison.categories) ? safeComparison.categories : [];

    // Auto-sync columns to match Live plans only. Keeps existing order and adds
    // new Live plans, removes Hidden/Off plans. Must run before early return.
    const livePlanKeys = Object.entries(plans || {})
        .filter(([, plan]) => plan.visible !== false && plan.enabled !== false)
        .map(([key]) => key);
    const currentColumns = safeComparison.columns || [];
    // Preserve order of existing columns, add new Live plans at the end
    const columns = [...new Set([
        ...currentColumns.filter((key) => livePlanKeys.includes(key)),
        ...livePlanKeys.filter((key) => !currentColumns.includes(key)),
    ])];

    // If columns changed, update comparison silently
    React.useEffect(() => {
        if (comparison && JSON.stringify(columns) !== JSON.stringify(currentColumns)) {
            onChange({ ...comparison, columns });
        }
    }, [columns, currentColumns, comparison, onChange]);

    if (!comparison) {
        return (
            <Box sx={{
                backgroundColor: theme.palette.customColors.whiteBG,
                border: `1px solid ${theme.palette.customColors.b50}`,
                borderRadius: '8px',
                padding: '2rem',
                textAlign: 'center',
            }}>
                <Typography sx={{ fontWeight: 700, color: theme.palette.customColors.g300 }}>
                    No comparison table yet
                </Typography>
                <Typography sx={{ fontSize: '0.875rem', color: theme.palette.customColors.g200, marginTop: 0.5 }}>
                    Run <strong>npm run plans:comparison</strong> once to bring across the table currently on the
                    pricing page. After that it is edited here.
                </Typography>
            </Box>
        );
    }

    // Only display columns for Live plans in the UI (data for hidden plans is preserved)
    const displayColumns = columns.filter((key) => livePlanKeys.includes(key));
    const gridTemplate = `minmax(16rem, 2fr) repeat(${Math.max(displayColumns.length, 1)}, minmax(7rem, 1fr)) 2.5rem`;
    const allRowIds = safeCategories.flatMap((category) => (category?.rows || []).map((row) => row?.id).filter(Boolean));
    const allCategoryIds = safeCategories.map((category) => category?.id).filter(Boolean);

    const update = (changes) => onChange({ ...safeComparison, ...changes });
    const updateCategory = (index, changes) => {
        const categories = [...safeCategories];
        if (!categories[index]) return;
        categories[index] = { ...categories[index], ...changes };
        update({ categories });
    };
    const updateRow = (categoryIndex, rowIndex, changes) => {
        const rows = [...(safeCategories[categoryIndex]?.rows || [])];
        if (!rows[rowIndex]) return;
        rows[rowIndex] = { ...rows[rowIndex], ...changes };
        updateCategory(categoryIndex, { rows });
    };

    const onDragEnd = (result) => {
        if (!result.destination) return;
        const categoryIndex = Number(result.source.droppableId.replace('rows-', ''));
        if (Number(result.destination.droppableId.replace('rows-', '')) !== categoryIndex) return;

        const rows = [...(safeCategories[categoryIndex]?.rows || [])];
        const [moved] = rows.splice(result.source.index, 1);
        if (!moved) return;
        rows.splice(result.destination.index, 0, moved);
        updateCategory(categoryIndex, { rows });
    };

    return (
        <Box display="grid" gap="1rem">
            <Box sx={{
                backgroundColor: theme.palette.customColors.whiteBG,
                border: `1px solid ${theme.palette.customColors.b50}`,
                borderRadius: '8px',
                padding: '1.25rem',
            }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={5}>
                        <CustomInputField label="Heading" value={comparison.heading || ''}
                            onChange={(event) => update({ heading: event.target.value })} sx={{ marginBottom: 0 }} />
                    </Grid>
                    <Grid item xs={12} md={7}>
                        <CustomInputField label="Subheading" value={comparison.subheading || ''}
                            onChange={(event) => update({ subheading: event.target.value })} sx={{ marginBottom: 0 }} />
                    </Grid>
                </Grid>
            </Box>

            <Box sx={{
                backgroundColor: theme.palette.customColors.whiteBG,
                border: `1px solid ${theme.palette.customColors.b50}`,
                borderRadius: '8px',
                overflow: 'hidden',
            }}>
                {/* Column header, held in view while the rows scroll past. Takes
                    the listing tables' header treatment — same tint, weight and
                    size as `thead th` in index.scss — so a table in settings
                    reads as the same kind of object as a table anywhere else. */}
                <Box sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    display: 'grid',
                    gridTemplateColumns: gridTemplate,
                    alignItems: 'center',
                    padding: '0.75rem 1rem',
                    backgroundColor: '#F4F2FE',
                    borderBottom: `1px solid ${theme.palette.customColors.b50}`,
                }}>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: 'black' }}>Feature</Typography>
                    {displayColumns.map((planKey) => (
                        <Typography key={planKey} component="div" sx={{
                            fontSize: '1rem',
                            fontWeight: 600,
                            color: 'black',
                            display: 'flex',
                            justifyContent: 'center',
                        }}>
                            <PlanLabel plans={plans} planKey={planKey} />
                        </Typography>
                    ))}
                    <span />
                </Box>

                <DragDropContext onDragEnd={onDragEnd}>
                    {safeCategories.map((category, categoryIndex) => (
                        <Box key={category?.id || `category-${categoryIndex}`}>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                padding: '0.75rem 1rem',
                                backgroundColor: '#fbfbfc',
                                borderBottom: `1px solid ${theme.palette.customColors.b50}`,
                            }}>
                                <Box sx={{ flex: 1, maxWidth: '22rem' }}>
                                    <CustomInputField
                                        value={category.title || ''}
                                        inputProps={{ 'aria-label': 'Section name' }}
                                        placeholder="Section name"
                                        onChange={(event) => updateCategory(categoryIndex, { title: event.target.value })}
                                        sx={{ marginBottom: 0, '& .MuiFilledInput-root': { fontWeight: 700 } }}
                                    />
                                </Box>
                                <Tooltip title="Open this section by default on mobile">
                                    <Chip
                                        size="small"
                                        label="Open by default"
                                        onClick={() => updateCategory(categoryIndex, { open_by_default: !category.open_by_default })}
                                        sx={{
                                            borderRadius: '6px',
                                            backgroundColor: category.open_by_default ? theme.palette.customColors.indigo50 : 'transparent',
                                            color: category.open_by_default ? theme.palette.customColors.indigo700 : theme.palette.customColors.g200,
                                            border: `1px solid ${category.open_by_default ? theme.palette.customColors.indigo700 : theme.palette.customColors.b50}`,
                                        }}
                                    />
                                </Tooltip>
                                <Box flex={1} />
                                <Tooltip title="Remove section">
                                    <IconButton size="small" sx={{ color: theme.palette.customColors.r400 }}
                                        onClick={() => update({ categories: comparison.categories.filter((_, i) => i !== categoryIndex) })}>
                                        <DeleteOutlineRounded fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>

                            <Droppable droppableId={`rows-${categoryIndex}`} direction="vertical">
                                {(dropProvided) => (
                                    <Box ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
                                        {(category?.rows || []).map((row, rowIndex) => {
                                            const rowProblems = problemsFor(`comparison.categories[${categoryIndex}].rows[${rowIndex}]`);
                                            if (!row?.id) return null;
                                            return (
                                                <Draggable key={row.id} draggableId={row.id} index={rowIndex}>
                                                    {(dragProvided, snapshot) => (
                                                        <Box
                                                            ref={dragProvided.innerRef}
                                                            {...dragProvided.draggableProps}
                                                            sx={{
                                                                display: 'grid',
                                                                gridTemplateColumns: gridTemplate,
                                                                alignItems: 'center',
                                                                gap: '0.5rem',
                                                                padding: '0.25rem 1rem',
                                                                borderBottom: `1px solid ${theme.palette.customColors.b50}`,
                                                                backgroundColor: snapshot.isDragging
                                                                    ? theme.palette.customColors.indigo50
                                                                    : rowProblems.length
                                                                        ? theme.palette.customColors.r50
                                                                        : theme.palette.customColors.whiteBG,
                                                                ...dragProvided.draggableProps.style,
                                                            }}
                                                        >
                                                            <Box display="flex" alignItems="center" gap={0.5}>
                                                                <Box {...dragProvided.dragHandleProps} sx={{ display: 'flex', cursor: 'grab', color: theme.palette.customColors.g75 }}>
                                                                    <DragIndicator fontSize="small" />
                                                                </Box>
                                                                <CustomInputField
                                                                    value={row.label || ''}
                                                                    inputProps={{ 'aria-label': 'Feature name' }}
                                                                    placeholder="Feature"
                                                                    onChange={(event) => updateRow(categoryIndex, rowIndex, { label: event.target.value })}
                                                                    sx={{
                                                                        marginBottom: 0,
                                                                        '& .MuiFilledInput-root': { border: 'none', background: 'transparent' },
                                                                        '& .MuiFilledInput-input': { padding: '8px 4px', fontSize: '0.875rem' },
                                                                    }}
                                                                />
                                                                <Tooltip title={row.info_icon === true ? 'Remove the info icon' : 'Add an info icon'}>
                                                                    <IconButton
                                                                        size="small"
                                                                        aria-pressed={row.info_icon === true}
                                                                        aria-label="Info icon"
                                                                        onClick={() => updateRow(categoryIndex, rowIndex, { info_icon: row.info_icon !== true })}
                                                                        sx={{ color: row.info_icon === true ? theme.palette.customColors.indigo700 : theme.palette.customColors.g75 }}
                                                                    >
                                                                        <InfoOutlined fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </Box>

                                                            {displayColumns.map((planKey) => (
                                                                <ValueCell
                                                                    key={planKey}
                                                                    label={`${row.label} for ${columnLabel(plans, planKey)}`}
                                                                    value={row.values?.[planKey] ?? false}
                                                                    onChange={(value) => updateRow(categoryIndex, rowIndex, {
                                                                        values: { ...(row.values || {}), [planKey]: value },
                                                                    })}
                                                                />
                                                            ))}

                                                            <Tooltip title="Remove feature">
                                                                <IconButton size="small" sx={{ color: theme.palette.customColors.g75, '&:hover': { color: theme.palette.customColors.r400 } }}
                                                                    onClick={() => updateCategory(categoryIndex, { rows: category.rows.filter((_, i) => i !== rowIndex) })}>
                                                                    <DeleteOutlineRounded fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            {/* Only offered while the row's info icon is on, since the icon is what shows it. */}
                                                            {row.info_icon === true && (
                                                                <Box sx={{ gridColumn: '1 / -1', paddingLeft: '1.75rem', paddingBottom: '0.5rem' }}>
                                                                    <CustomInputField
                                                                        label="Info (shown on hover or tap)"
                                                                        multiline
                                                                        minRows={2}
                                                                        value={row.info || ''}
                                                                        inputProps={{ 'aria-label': `${row.label} info` }}
                                                                        placeholder="Write what the visitor should read on the info icon"
                                                                        onChange={(event) => updateRow(categoryIndex, rowIndex, { info: event.target.value })}
                                                                        sx={{ marginBottom: 0 }}
                                                                    />
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    )}
                                                </Draggable>
                                            );
                                        })}
                                        {dropProvided.placeholder}

                                        <Box
                                            onClick={() => {
                                                const label = '';
                                                updateCategory(categoryIndex, {
                                                    rows: [...(category?.rows || []), {
                                                        id: slugify(`feature_${(category?.rows || []).length + 1}`, allRowIds),
                                                        label,
                                                        values: Object.fromEntries(columns.map((planKey) => [planKey, false])),
                                                    }],
                                                });
                                            }}
                                            sx={{
                                                display: 'flex', alignItems: 'center', gap: 0.5,
                                                padding: '0.625rem 1rem 0.625rem 2.25rem',
                                                cursor: 'pointer',
                                                color: theme.palette.customColors.g100,
                                                borderBottom: `1px solid ${theme.palette.customColors.b50}`,
                                                '&:hover': { color: theme.palette.customColors.indigo700, backgroundColor: '#fbfbfc' },
                                            }}
                                        >
                                            <Add fontSize="small" />
                                            <Typography sx={{ fontSize: '0.875rem' }}>Add feature</Typography>
                                        </Box>
                                    </Box>
                                )}
                            </Droppable>
                        </Box>
                    ))}
                </DragDropContext>

                <Box padding="1rem">
                    <Secondary startIcon={<Add />} onClick={() => update({
                        categories: [...safeCategories, {
                            id: slugify(`section_${safeCategories.length + 1}`, allCategoryIds),
                            title: '',
                            open_by_default: false,
                            rows: [],
                        }],
                    })}>
                        Add section
                    </Secondary>
                </Box>
            </Box>
        </Box>
    );
};

export default ComparisonEditor;
